/**
 * A ficha da leitura do caso na tela (E139.F3, ADR-0095 §2.7): lê ao montar e a cada `releitura`
 * (o caso relido depois do exame, documento novo), e relê sozinha enquanto há página-imagem em
 * leitura — a leitura roda em segundo plano e regrava a ficha quando termina (§2.6).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AxiosInstance } from 'axios';

import {
  FichaDaLeituraError,
  lerFichaDaLeitura,
  temPaginaEmLeitura,
  type FichaDaLeituraOutData,
  type MotivoDaFalhaDaFicha,
} from '@/lib/ficha_da_leitura';

export const INTERVALO_DA_RELEITURA_MS = 20_000;
export const RELEITURAS_MAXIMAS = 9;

export type EstadoDaFicha =
  | { estado: 'carregando' }
  | { estado: 'vazio' }
  | { estado: 'erro'; motivo: MotivoDaFalhaDaFicha }
  | { estado: 'pronto'; dados: FichaDaLeituraOutData };

export function useFichaDaLeitura(
  client: AxiosInstance,
  caseId: string,
  releitura: unknown,
): { ficha: EstadoDaFicha; reler: () => void } {
  const [ficha, setFicha] = useState<EstadoDaFicha>({ estado: 'carregando' });
  const [pedido, setPedido] = useState(0);
  const relidas = useRef(0);

  const reler = useCallback(() => setPedido((n) => n + 1), []);

  useEffect(() => {
    relidas.current = 0;
  }, [caseId, releitura]);

  useEffect(() => {
    let vivo = true;
    let espera: ReturnType<typeof setTimeout> | undefined;
    void (async () => {
      try {
        const dados = await lerFichaDaLeitura(client, caseId);
        if (!vivo) return;
        setFicha(dados === null ? { estado: 'vazio' } : { estado: 'pronto', dados });
        if (dados !== null && temPaginaEmLeitura(dados.ficha.ficha)) {
          if (relidas.current < RELEITURAS_MAXIMAS) {
            relidas.current += 1;
            espera = setTimeout(reler, INTERVALO_DA_RELEITURA_MS);
          }
        }
      } catch (err) {
        if (!vivo) return;
        const motivo = err instanceof FichaDaLeituraError ? err.motivo : 'indisponivel';
        setFicha({ estado: 'erro', motivo });
      }
    })();
    return () => {
      vivo = false;
      if (espera !== undefined) clearTimeout(espera);
    };
  }, [client, caseId, releitura, pedido, reler]);

  return { ficha, reler };
}
